import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '@itsrever/design-system'
import { useTranslation } from 'react-i18next'
import {
  createApiKey,
  getApiKeyValue,
  listApiKeys,
  revokeApiKey
} from '@/api/settings-api'

const API_KEYS_KEY = ['apiKeys']

function keysKey(groups: string[]) {
  return [...API_KEYS_KEY, ...groups]
}

// Metadata only, never a key value, so it is safe to keep in the query cache.
// An empty list of groups asks the server for the caller's own group; a REVER staff caller has
// none, so it must name at least one and the query stays idle until it does.
export function useApiKeys(groups: string[], enabled: boolean) {
  const query = useQuery({
    queryKey: keysKey(groups),
    queryFn: () => listApiKeys(groups.length ? groups : undefined),
    enabled
  })

  return {
    apiKeys: query.data?.keys ?? [],
    loadingApiKeys: query.isLoading,
    apiKeysError: query.isError,
    reloadApiKeys: query.refetch
  }
}


export function useCreateApiKey() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    // The result carries the plaintext, so it must not outlive the component that asked for it.
    gcTime: 0,
    mutationFn: (input: { group: string; name: string; scopes: string[] }) =>
      createApiKey(input.group, input.name, input.scopes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: API_KEYS_KEY })
    },
    onError: () =>
      toast({ text: t('api_keys.create_error'), variant: 'error' })
  })

  return {
    createKey: mutation.mutateAsync,
    creating: mutation.isPending,
    // Carries the plaintext of the key just issued. Reset once the dialog that shows it closes.
    created: mutation.data,
    clearCreated: mutation.reset
  }
}

// Reading is deliberately not cached: the plaintext goes to the component that asked for it and
// nothing else, so it cannot outlive the click that requested it.
export function useApiKeyValue() {
  const { t } = useTranslation()

  const mutation = useMutation({
    gcTime: 0,
    mutationFn: (input: { group: string; name: string }) =>
      getApiKeyValue(input.group, input.name),
    onError: () => toast({ text: t('api_keys.read_error'), variant: 'error' })
  })

  return {
    readKeyValue: mutation.mutateAsync,
    keyValue: mutation.data?.value,
    reading: mutation.isPending,
    clearKeyValue: mutation.reset
  }
}

export function useRevokeApiKey() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (input: { group: string; name: string }) =>
      revokeApiKey(input.group, input.name),
    onSuccess: () => {
      toast({ text: t('api_keys.deleted'), variant: 'success' })
      queryClient.invalidateQueries({ queryKey: API_KEYS_KEY })
    },
    onError: () =>
      toast({ text: t('api_keys.delete_error'), variant: 'error' })
  })

  return {
    revokeKey: mutation.mutateAsync,
    revoking: mutation.isPending
  }
}
